import { motion } from "framer-motion"
import Head from "next/head"
import { Button } from "../components/Button"
import { Footer } from "../components/Footer"
import { Header } from "../components/Header"
import { PageProps } from "../utils/types"

const headerVariants = {
  animate: {
    opacity: 1,
  },
  initial: {
    opacity: 0,
  },
}

const rowVariants1 = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.05,
    },
  },
  initial: {
    opacity: 0,
  },
}

const rowVariants2 = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.1,
    },
  },
  initial: {
    opacity: 0,
  },
}

const rowVariants3 = {
  animate: {
    opacity: 1,
    transition: {
      delay: 0.15,
    },
  },
  initial: {
    opacity: 0,
  },
}

export default function InstallPage(props: PageProps) {
  return (
    <>
      <Head>
        <title>Frontender | Move faster.</title>
        <meta name="theme-color" content="#18181b" />
        <meta property="og:image" content="https://www.frontender.io/og.png" />
        <meta property="og:image:type" content="image/png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="627" />
        <meta property="og:title" content="Frontender" />
        <meta property="description" content="Move faster." />
        <meta property="og:description" content="Move faster." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.frontender.io/" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <meta name="msapplication-TileColor" content="#da532c" />
      </Head>
      <motion.div
        initial="initial"
        animate="animate"
        className="flex min-h-screen select-none bg-gradient-to-b from-zinc-900 via-zinc-900 to-zinc-800 p-4"
      >
        <div className="flex flex-1 flex-col overflow-hidden border-zinc-700 p-4 pt-1 sm:border sm:p-7 sm:pt-3">
          <Header />
          <div className="max-w-screen-2xl py-10 sm:px-4 lg:px-12 lg:py-16 xl:py-20">
            <motion.div
              variants={headerVariants}
              className="mb-10 inline-block bg-gradient-to-r from-sky-300 to-fuchsia-300 bg-clip-text text-3xl font-semibold leading-tight text-transparent sm:mb-16 sm:text-5xl sm:tracking-tight"
            >
              Get started within 30 seconds.
            </motion.div>
            <div className="grid gap-16 pb-6 lg:grid-cols-3 lg:pb-0 xl:gap-10">
              <motion.div variants={rowVariants1} className="flex">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-sky-300 text-lg font-bold text-sky-900">
                  1
                </div>
                <div className="px-6 py-1.5">
                  <div className="text-lg font-medium text-white">
                    Get the plugin
                  </div>
                  <p className="mb-4 mt-1 leading-relaxed text-zinc-400">
                    Go to the plugin page in the Figma Community and click
                    &quot;Try it out&quot;. It&apos;s free!
                  </p>
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://www.figma.com/community/plugin/1194222042313126344"
                    className="inline-block xl:w-48"
                  >
                    <Button>
                      <span className="flex items-center justify-center space-x-3">
                        <svg
                          width="172"
                          height="247"
                          viewBox="0 0 172 247"
                          fill="none"
                          className="h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g id="Group">
                            <path
                              id="Rectangle 55"
                              d="M164 46.5C164 67.763 146.763 85 125.5 85L86 85L86 8.0001L125.5 8.0001C146.763 8.0001 164 25.2371 164 46.5V46.5Z"
                              stroke="white"
                              strokeWidth="15.081"
                            />
                            <path
                              id="Rectangle 55_2"
                              d="M8 46.5C8 67.763 25.237 85 46.5 85L86 85L86 8.0001L46.5 8.0001C25.237 8.0001 8 25.2371 8 46.5V46.5Z"
                              stroke="white"
                              strokeWidth="15.081"
                            />
                            <path
                              id="Rectangle 55_3"
                              d="M8 123.501C8 144.764 25.237 162.001 46.5 162.001H86L86 85.0011L46.5 85.0011C25.237 85.0011 8 102.238 8 123.501V123.501Z"
                              stroke="white"
                              strokeWidth="15.081"
                            />
                            <path
                              id="Rectangle 55_4"
                              d="M8 200.5C8 221.763 25.4854 239 46.7484 239V239C68.2875 239 86 221.539 86 200L86 162H46.5C25.237 162 8 179.237 8 200.5V200.5Z"
                              stroke="white"
                              strokeWidth="15.081"
                            />
                            <path
                              id="Rectangle 55_5"
                              d="M86 123.501C86 144.764 103.237 162.001 124.5 162.001H125.5C146.763 162.001 164 144.764 164 123.501V123.501C164 102.238 146.763 85.0011 125.5 85.0011H124.5C103.237 85.0011 86 102.238 86 123.501V123.501Z"
                              stroke="white"
                              strokeWidth="15.081"
                            />
                          </g>
                        </svg>
                        <div>Get the plugin</div>
                      </span>
                    </Button>
                  </a>
                  <div
                    className="mt-8 overflow-hidden rounded-lg border border-zinc-800 xl:mt-12"
                    style={{ maxWidth: 280 }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="/s1.png"
                      alt="Figma try it out button"
                      width={280}
                      height={190}
                      className="shadow"
                    />
                  </div>
                </div>
              </motion.div>

              <motion.div variants={rowVariants2} className="flex">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-purple-300 font-bold text-purple-900">
                  2
                </div>
                <div className="px-6 py-1.5">
                  <div className="text-lg font-medium text-white">
                    Add to your Figma file
                  </div>
                  <p className="mt-1 leading-relaxed text-zinc-400">
                    In your Figma file, click on the{" "}
                    <svg
                      width="26"
                      height="26"
                      viewBox="0 0 26 26"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mx-1.5 my-1 inline-flex h-6 w-6 items-center justify-center rounded border border-zinc-400 text-xl text-zinc-200"
                    >
                      <rect
                        x="5.49265"
                        y="4.5"
                        width="5.5"
                        height="5.5"
                        stroke="currentColor"
                      />
                      <path
                        d="M4.70711 17.1499L8.24264 13.6143L11.7782 17.1499L8.24264 20.6854L4.70711 17.1499Z"
                        stroke="currentColor"
                      />
                      <rect
                        x="15.25"
                        y="14.3999"
                        width="5.5"
                        height="5.5"
                        stroke="currentColor"
                      />
                      <rect
                        x="17.5"
                        y="4"
                        width="1"
                        height="6.5"
                        fill="currentColor"
                      />
                      <rect
                        x="14.75"
                        y="7.75"
                        width="1"
                        height="6.5"
                        transform="rotate(-90 14.75 7.75)"
                        fill="currentColor"
                      />
                    </svg>{" "}
                    icon (or press Shift-<code>I</code>). Navigate to plugins
                    and run Frontender. You&apos;re ready to go!
                  </p>
                  <div
                    className="mt-8 overflow-hidden rounded-lg border border-zinc-800 xl:mt-12"
                    style={{ maxWidth: 280 }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="/run.png"
                      alt="Figma run plugin"
                      width={280}
                      height={190}
                      className="shadow"
                    />
                  </div>
                </div>
              </motion.div>

              <motion.div variants={rowVariants3} className="flex">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-300 font-bold text-emerald-900">
                  3
                </div>
                <div className="px-6 py-1.5">
                  <div className="text-lg font-medium text-white">
                    Tips for quick access
                  </div>
                  <p className="mt-3 leading-relaxed text-zinc-400">
                    In that same menu, click on the Frontender plugin name (not
                    Run) to open the plugin details. Click on{" "}
                    <span className="m-1 inline-flex h-6 w-6 items-center justify-center rounded border border-zinc-400 text-xl text-zinc-200">
                      &middot;&middot;&middot;
                    </span>
                    , then Save, to always have quick access to Frontender.
                  </p>
                  <p className="mt-3 leading-relaxed text-zinc-400">
                    To access Frontender even faster, press Ctrl&#8209;
                    <code>P</code> (Windows) or Command&#8209;<code>P</code>{" "}
                    (Mac) and type Frontender to quickly launch the plugin.
                  </p>
                  <div
                    className="mt-8 overflow-hidden rounded-lg border border-zinc-800 xl:mt-12"
                    style={{ maxWidth: 280 }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src="/save.png"
                      alt="Figma save plugin"
                      width={280}
                      height={190}
                      className="shadow"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
          <Footer
            acceptedCookies={props.acceptedCookies}
            setAcceptedCookies={props.setAcceptedCookies}
          />
        </div>
        <div className="animate-blob pointer-events-none fixed -right-[60vw] top-[20vh] h-[120vh] w-[120vw] rounded-full bg-fuchsia-800 opacity-10 blur-3xl" />
      </motion.div>
    </>
  )
}

InstallPage.displayName = "InstallPage"
